'use client';

import { FC, useMemo } from 'react';
import classNames from 'classnames';
import * as prismicH from '@prismicio/helpers';
import { Content } from '@prismicio/client';

import { Heading, Link, useAuth, useData } from '@/components';
import { useCustomerOrderHistory } from '@/data';
import { useShowByLocationSlug } from '@/hooks';
import { PAGE_ROUTES } from '@/constants';
import { Order } from '@/types';
import { getSlicePadding } from '@/utils';

import { ReorderCarousel } from './ReorderCarousel';
import { getReorderItemIds, useReorderProducts } from './useReorderProducts';

import styles from './reorder.module.scss';

const DEFAULT_LIMIT = 14;
const SKELETON_COUNT = 20;
const ORDER_HISTORY_HREF = `${PAGE_ROUTES.ACCOUNT}?section=orders`;

const EXCLUDED_ORDER_STATUSES = new Set(['DECLINED', 'VOID']);

const collectRecentItemIds = (orders: Order[]): string[] => {
  const seen = new Set<string>();

  return orders
    .filter(order => !EXCLUDED_ORDER_STATUSES.has(order?.status ?? ''))
    .flatMap(getReorderItemIds)
    .filter(id => (seen.has(id) ? false : seen.add(id)));
};

const ReorderSlice: FC<Content.ReorderSlice> = slice => {
  const { primary, variation } = slice;
  const { isAuth, email } = useAuth();
  const { productCard } = useData();
  const {
    breakpoints,
    defaultSlidePerView,
    defaultSpaceBetween,
    spacingBetweenCard,
    isLargeCard,
  } = productCard.displayProps;
  const { showContent } = useShowByLocationSlug(
    primary.location_slug as string,
  );

  const { orders, loading: isLoadingOrders } = useCustomerOrderHistory({
    skip: !isAuth || !email,
    variables: { email },
  });

  const itemIds = useMemo(
    () => collectRecentItemIds((orders ?? []) as Order[]),
    [orders],
  );

  const { isLoading: isLoadingProducts, products } = useReorderProducts(
    itemIds,
    { fallbackToDefaultStore: true },
  );

  const isLoading = isLoadingOrders || isLoadingProducts;

  const visibleProducts = useMemo(() => {
    const rank = new Map(itemIds.map((id, index) => [id, index]));

    return [...products]
      .sort(
        (a, b) =>
          (rank.get(a.id as string) ?? Infinity) -
          (rank.get(b.id as string) ?? Infinity),
      )
      .slice(0, primary.limit || DEFAULT_LIMIT);
  }, [products, itemIds, primary.limit]);

  if (variation !== 'default') return null;
  if (!primary.active || !showContent || !isAuth) return null;
  if (!isLoading && visibleProducts.length === 0) return null;

  const title = prismicH.asText(primary.title) || 'Buy it again';
  const ariaLabel = `${title} carousel`;

  return (
    <section
      aria-label={ariaLabel}
      className={classNames(
        styles.reorder__slice,
        getSlicePadding(primary.padding_section),
        {
          ['spacing_bottom']: primary.bottom_spacing,
          ['background__fixed_cover']: primary.background_style_fixed_cover,
          ['background__contained']: !primary.background_style_fixed_cover,
        },
      )}
      style={{
        background: primary.background_color!,
        backgroundImage: primary.background_image?.url
          ? `url(${primary.background_image.url})`
          : undefined,
        minHeight: `${primary.min_height || 0}vh`,
      }}
    >
      <ReorderCarousel
        activatedHover
        breakpoints={breakpoints}
        isLargeCard={isLargeCard}
        isLoading={isLoading}
        isNotPaddingMobile={spacingBetweenCard === 'none'}
        products={visibleProducts}
        seeAllElement={
          primary.see_all_text ? (
            <Link
              href={ORDER_HISTORY_HREF}
              style={{ color: primary.see_all_text_color! }}
            >
              {primary.see_all_text}
            </Link>
          ) : undefined
        }
        showSliderHeader
        showSlidesOutsideContainer={false}
        skeletonCount={SKELETON_COUNT}
        sliderHeading={
          <Heading level={2} style={{ color: primary.title_color! }}>
            {title}
          </Heading>
        }
        slidesPerView={defaultSlidePerView}
        spaceBetween={defaultSpaceBetween}
      />
      <style>
        {`.${styles.reorder__slice} {
            .slick-arrow {
              color: ${primary.active_control_color};

              &:hover {
                color: ${primary.active_control_color};
              }

              &.slick-disabled {
                color: ${primary.disable_control_color};
              }
            }
          }
        `}
      </style>
    </section>
  );
};

export { ReorderSlice };
